import { List, useMediaQuery, useTheme } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';

import { RouteContext, SidebarThemeContext } from 'src/context';
import { MobileCompanySwitcher } from 'src/features/companies/CompanySwitcher';
import { useCompanySwitcherVisibility } from 'src/features/companies/useCompanySwitcherVisibility';
import { Drawer } from 'src/features/drawer/Drawer';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { RootState } from 'src/store';

import { SettingsSidebarBackToggle } from './SettingsSidebarBackToggle';
import { SidebarLogoContainer } from './SidebarLogoContainer';
import SidebarNav from './SidebarNav';
import { useSidebarTheme } from './useSidebarTheme';

const SidebarSkeletonItems = [
  { id: 'crm' },
  { id: 'messaging' },
  { id: 'files' },
];

interface SidebarProps {
  openMobile: boolean;
  onMobileClose: () => void;
}

const SidebarContainer: React.FC<SidebarProps> = ({
  openMobile,
  onMobileClose,
}) => {
  const { sidebarTheme } = useSidebarTheme();
  const isSettingsLoaded = useSelector(
    (state: RootState) => state.settings.loaded,
  );
  const isExtensionsItemsLoaded = useSelector(
    (state: RootState) => state.dashboard.itemsLoaded,
  );
  const { isLoading: isLoadingInstalls } = useGetInstallsQuery('withFolders');
  const { visibility: companySwitcherVisibility } =
    useCompanySwitcherVisibility();
  const { pathname } = useContext(RouteContext);
  const theme = useTheme();
  const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // show sidebar skeleton if app state is not ready
  // or settings are not loaded
  // or extensions items are not loaded yet
  // or RTK Query is still loading installs data
  const showSidebarSkeleton =
    !isSettingsLoaded || !isExtensionsItemsLoaded || isLoadingInstalls;
  const isSettingsPage = pathname.includes('settings');

  return (
    <SidebarThemeContext.Provider value={sidebarTheme}>
      <Drawer onClose={onMobileClose} open={openMobile}>
        <div className="pt-4 px-2 pb-7">
          {/* On main sidebar show portal logo */}
          {companySwitcherVisibility === 'mobile' ? (
            <MobileCompanySwitcher />
          ) : (
            <SidebarLogoContainer isSettingsPage={isSettingsPage} />
          )}
          {/* On settings sidebar show back to portal button only on desktop */}
          {isSettingsPage && !isSmScreen && <SettingsSidebarBackToggle />}
        </div>
        <List className="flex flex-col pb-1 h-full">
          {showSidebarSkeleton ? (
            SidebarSkeletonItems.map(({ id }) => (
              <div key={id} className="py-1 px-2">
                <Skeleton variant="rect" width="100%" height={28} />
              </div>
            ))
          ) : (
            <SidebarNav />
          )}
        </List>
      </Drawer>
    </SidebarThemeContext.Provider>
  );
};

export default SidebarContainer;
